import { apiClient } from '@/api/client'
import {
  AUDIT_LOGS,
  auditLogByIdPath,
  auditLogsExportCsvPath,
} from '@/api/endpoints'
import type {
  AuditLog,
  AuditLogQueryParams,
} from '@/types'
import type { ApiResponseWrapper } from '@/types/api.types'
import type { PaginatedResponse } from '@/types'

/** API returns { data: { data: [], total, page, limit } } with no totalPages. */
function toPaginatedResponse(inner: {
  data: AuditLog[]
  total: number
  page: number
  limit: number
  totalPages?: number
}): PaginatedResponse<AuditLog> {
  const total = Number(inner.total) || 0
  const limit = Number(inner.limit) || 10
  const totalPages =
    inner.totalPages != null && inner.totalPages > 0
      ? inner.totalPages
      : Math.ceil(total / limit) || 0
  return {
    data: Array.isArray(inner.data) ? inner.data : [],
    total,
    page: Number(inner.page) || 1,
    limit,
    totalPages,
  }
}

export async function getAuditLogs(
  params?: AuditLogQueryParams
): Promise<PaginatedResponse<AuditLog>> {
  const { data } = await apiClient.get<
    ApiResponseWrapper<{
      data: AuditLog[]
      total: number
      page: number
      limit: number
      totalPages?: number
    }>
  >(AUDIT_LOGS, { params })
  const payload = data?.data
  if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
    return toPaginatedResponse(payload)
  }
  return toPaginatedResponse({
    data: [],
    total: 0,
    page: 1,
    limit: params?.limit ?? 10,
  })
}

export async function getAuditLogById(id: string): Promise<AuditLog> {
  const { data } = await apiClient.get<ApiResponseWrapper<AuditLog>>(
    auditLogByIdPath(id)
  )
  if (data?.data && typeof data.data === 'object' && data.data._id) {
    return data.data
  }
  throw new Error('Audit log not found')
}

export async function exportAuditLogsCsv(
  params?: AuditLogQueryParams
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    auditLogsExportCsvPath(),
    {
      params,
      responseType: 'blob',
    }
  )
  return data
}
